import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  useListPtoProjectsQuery,
  useCreatePtoProjectMutation,
  useDeletePtoProjectMutation,
  useRestorePtoProjectMutation,
  type PtoProject,
} from '@shared/api';
import { useAgentPermission, usePtoDeleteAccess } from '@entities/user';
import { deletionDaysLeft, DeletionBadge } from './deletion';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Card, CardTitle, CardSubtitle } from '@shared/ui/Card';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const ProjectLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const CreateRow = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  align-items: flex-end;
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const StatusTag = styled.span<{ $archived: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $archived, theme }) =>
    $archived ? theme.colors.text.secondary : theme.colors.primary};
`;

export function PtoProjects() {
  const { can_write } = useAgentPermission('pto');
  const canDelete = usePtoDeleteAccess();
  const { activeProjectId, setActiveProjectId: setProjectId } = useActiveProjectContext();
  const [showDeleted, setShowDeleted] = useState(false);
  const { data, isLoading } = useListPtoProjectsQuery(
    showDeleted ? { deleted: true } : undefined,
  );
  const [createProject, { isLoading: creating }] = useCreatePtoProjectMutation();
  const [deleteProject] = useDeletePtoProjectMutation();
  const [restoreProject] = useRestorePtoProjectMutation();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const projects: PtoProject[] = data?.items ?? [];
  const activeProject = projects.find((p) => p.id === activeProjectId);

  async function onDelete(e: React.MouseEvent, id: string) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Пометить проект на удаление? Будет удалён через 30 дней; до этого можно восстановить.'))
      return;
    try {
      await deleteProject(id).unwrap();
    } catch {
      setError('Не удалось пометить на удаление (нужна роль Инженер+ или Администратор).');
    }
  }

  async function onRestore(e: React.MouseEvent, id: string) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await restoreProject(id).unwrap();
    } catch {
      setError('Не удалось восстановить проект.');
    }
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return;
    try {
      await createProject({ name: name.trim(), code: code.trim() || undefined }).unwrap();
      setName('');
      setCode('');
    } catch {
      setError('Не удалось создать проект. Проверьте права и уникальность кода.');
    }
  }

  return (
    <div>
      {can_write && (
        <CreateRow onSubmit={onCreate}>
          <Field style={{ flex: '2 1 240px' }}>
            Название объекта
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="БОВ 40 000 м³/ч, ДГП-2"
            />
          </Field>
          <Field style={{ flex: '1 1 140px' }}>
            Код (опц.)
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="DGP2-BOV" />
          </Field>
          <Button $variant="primary" type="submit" disabled={creating || !name.trim()}>
            {creating ? 'Создание…' : 'Создать проект'}
          </Button>
          <Link to="/agents/pto/rd-import">
            <Button $variant="secondary" type="button">
              Загрузить РД архивом
            </Button>
          </Link>
        </CreateRow>
      )}
      {error && <Muted style={{ color: 'crimson' }}>{error}</Muted>}

      {canDelete && (
        <Muted>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Показать помеченные на удаление
          </label>
        </Muted>
      )}

      {activeProject && (
        <Muted>
          Активный объект:{' '}
          <ProjectLink
            to={`/agents/pto/${activeProject.id}`}
            onClick={() => setProjectId(activeProject.id)}
            style={{ fontWeight: 700, textDecoration: 'underline' }}
          >
            {activeProject.name}
          </ProjectLink>
        </Muted>
      )}

      {isLoading ? (
        <Muted>Загрузка проектов…</Muted>
      ) : projects.length === 0 ? (
        <Muted>Проектов пока нет. {can_write ? 'Создайте первый выше.' : ''}</Muted>
      ) : (
        <Grid>
          {projects.map((p) => (
            <ProjectLink
              key={p.id}
              to={`/agents/pto/${p.id}`}
              onClick={() => setProjectId(p.id)}
            >
              <Card>
                <CardTitle>{p.name}</CardTitle>
                <CardSubtitle>
                  {p.code ? `${p.code} · ` : ''}
                  {p.deleted_at ? (
                    <DeletionBadge>удаляется · осталось {deletionDaysLeft(p.deleted_at)} дн.</DeletionBadge>
                  ) : (
                    <StatusTag $archived={p.status !== 'active'}>
                      {p.status === 'active' ? 'активен' : 'в архиве'}
                    </StatusTag>
                  )}
                </CardSubtitle>
                {canDelete && (
                  <div style={{ marginTop: 8 }}>
                    {p.deleted_at ? (
                      <Button $variant="ghost" $size="sm" onClick={(e) => onRestore(e, p.id)}>
                        Восстановить
                      </Button>
                    ) : (
                      <Button $variant="ghost" $size="sm" onClick={(e) => onDelete(e, p.id)}>
                        Удалить
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            </ProjectLink>
          ))}
        </Grid>
      )}
    </div>
  );
}
